import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Grid,
  Box,
  TablePagination,
} from "@mui/material";
import DataTable from "../../Table/DataTable";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import Moment from "moment";
import { SESSION_AUTH } from "../../../helpers/auth";
import { getModuleAllPermissions } from "../../../helpers/utils";
import { useNavigate } from "react-router-dom";
import { ToastManager } from "../../common/ToastContainer";
import { EVENTS_SERVICES } from "../../../services";
import FullPageProgress from "../../common/FullPageProgress";
import { BarChart } from "@mui/x-charts";

export default function EventNews(props) {
  const { module } = props;
  const { userSession, LOGOUT_USER_SESSION } = SESSION_AUTH();
  const navigate = useNavigate();
  const [dataFetched, setDataFetched] = useState(false);
  const [userData, setUserData] = useState("");
  const [loader, setLoader] = useState(false);
  const [modulePermissions, setModulePermissions] = useState();
  const [userList, setUserList] = useState([]);

  const [totalRows, setTotalRows] = useState(0);

  const [chartData, setChartData] = useState("");

  const [dataset, setDataset] = useState([]);

  const defaultFilter = {
    page: 0,
    limit: 10,
    title: "",
  };
  const [filter, setFilter] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let sufilter = { ...defaultFilter };
    for (const [key, value] of urlParams.entries()) {
      if (value) {
        sufilter[key] =
          value === "null"
            ? null
            : !isNaN(value) && value.trim() !== ""
              ? Number(value)
              : value;
      }
    }
    return sufilter;
  });

  const handleChangePage = (event, newPage) => {
    setFilter((old) => ({
      ...old,
      page: newPage,
    }));
    setDataFetched(false);
  };

  const handleChangeRowsPerPage = (event) => {
    setFilter((old) => ({
      ...old,
      limit: event.target.value,
      page: 0,
    }));
    setDataFetched(false);
  };

  useEffect(() => {
    if (userSession) {
      setModulePermissions(getModuleAllPermissions(userSession, module));
      setUserData(userSession);
    } else {
      setModulePermissions("");
      setUserData("");
    }
    return () => null;
  }, [userSession, module]);

  useEffect(() => {
    if (!userData || dataFetched || !modulePermissions?.view) return;

    setLoader(true);
    setDataFetched(true);

    let parma = { ...filter };
    parma["userId"] = userData.userId;
    EVENTS_SERVICES.getNews(parma)
      .then((data) => {
        setUserList([]);
        if (data.code === 200) {
          setUserList(data?.data?.list || []);
          if (data?.data.pageInfo.page === 0) {
            setTotalRows(data?.data.pageInfo?.count || 0);
          }
        } else if (data.code === 600) {
          LOGOUT_USER_SESSION();
          ToastManager.addToast(data?.message);
        } else {
          ToastManager.addToast(data?.message);
        }
      })
      .catch((error) => {
        ToastManager.addToast(error.message);
      })
      .finally(() => {
        setLoader(false);
      });
  }, [userData, dataFetched, LOGOUT_USER_SESSION, modulePermissions, filter]);

  useEffect(() => {
    if (userData && !modulePermissions?.view) {
      return navigate("/");
    }
  }, [modulePermissions, userData, navigate]);

  const columns = [
    { name: "sno", label: "S.No." },

    {
      name: "title",
      label: "Title",
      options: {
        customBodyRender: (value) => {
          return (
            <Link
              to={`${process.env.REACT_APP_EVENT_PLATFORM}/news/${value?.slug}`}
              target="_blank"
              className="text-link"
              style={{ whiteSpace: "normal" }}
            >
              {value?.title}
            </Link>
          );
        },
      },
    },
    {
      name: "org",
      label: "Organization",
      options: {
        customBodyRender: (item) => {
          return (
            <Link
              to={`/events/organization/${item?._id}/news`}
              className="text-link"
            >
              {item?.orgName}
            </Link>
          );
        },
      },
    },

    { name: "createdAt", label: "Created" },
    { name: "source", label: "Source" },
  ];
  const customOption = {
    pagination: false,
    sort: false,
  };

  const clearFilter = () => {
    setFilter(defaultFilter);
    setDataFetched(false);
  };

  const onChangeFilter = (name, value) => {
    let f = { ...filter };
    f[name] = value;
    setFilter(f);
  };
  const applyFilter = () => {
    setFilter((old) => ({
      ...old,
      page: 0,
    }));
    setDataFetched(false);
  };

  useEffect(() => {
    if (!userData || dataFetched || !modulePermissions?.view || chartData)
      return;

    setLoader(true);
    setDataFetched(true);

    EVENTS_SERVICES.newsChart({ userId: userData.userId })
      .then((data) => {
        if (data.code === 200) {
          setChartData(data?.data);
        } else if (data.code === 600) {
          LOGOUT_USER_SESSION();
          ToastManager.addToast(data?.message);
        } else {
          ToastManager.addToast(data?.message);
        }
      })
      .catch((error) => {
        ToastManager.addToast(error.message);
      })
      .finally(() => {
        setLoader(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, dataFetched, LOGOUT_USER_SESSION, modulePermissions]);

  useEffect(() => {
    if (!Array.isArray(chartData)) {
      setDataset([]);

      return;
    }

    // format weekday
    const formatDay = (dateStr) =>
      new Date(dateStr).toLocaleDateString("en-US", {
        weekday: "short",
      });

    // ✅ build dataset for MUI
    const data = chartData.map((item) => ({
      day: formatDay(item._id),
      value: Number(item.count) || 0,
    }));

    setDataset(data);
  }, [chartData]);

  return (
    <>
      {loader && <FullPageProgress />}
      <Box className="managepro6ix-layout-page-content">
        <div className="managepro6ix-layout-page-content-header">
          <div className="managepro6ix-layout-page-content-header-left">
            <div className="managepro6ix-layout-page-content-header-heading">
              6ix Press Release
            </div>
          </div>
        </div>
        {dataset?.length > 0 ? (
          <Grid item xs={12} md={12}>
            <BarChart
              height={300}
              dataset={dataset}
              colors={["#A78F77"]}
              xAxis={[
                {
                  scaleType: "band",
                  dataKey: "day",
                },
              ]}
              series={[
                {
                  dataKey: "value",
                },
              ]}
            />
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, textAlign: "center" }}
            >
              Last 7 Days
            </Typography>
          </Grid>
        ) : null}

        <Accordion defaultExpanded>
          <AccordionSummary
            sx={{ marginTop: 2, width: "100px" }}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography sx={{ textTransform: "uppercase", fontSize: "14px" }}>
              Filters
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={{ xs: 1, md: 2 }}>
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  label="Search by news title"
                  value={filter?.title}
                  onChange={(e) => onChangeFilter("title", e.target.value)}
                />
              </Grid>

              <Grid item sx={{ marginTop: 1 }}>
                <button
                  type="button"
                  className="managepro6ix-btn managepro6ix-btn-primary"
                  onClick={applyFilter}
                >
                  Search
                </button>
              </Grid>
              <Grid item sx={{ marginTop: 1 }}>
                <button
                  type="button"
                  className="managepro6ix-btn managepro6ix-btn-primary"
                  onClick={clearFilter}
                >
                  Clear
                </button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <DataTable
          customOption={customOption}
          columns={columns}
          data={userList.map((item, key) => {
            return {
              sno: filter?.page * filter?.limit + key + 1,
              org: item?.accountId,
              title: { title: item?.title, slug: item?.slug },
              createdAt: Moment(item?.createdAt).format(
                "MMM DD, YYYY : hh:mm A",
              ),
              source: item?.source,
            };
          })}
        />

        <TablePagination
          component="div"
          count={totalRows}
          page={filter.page}
          onPageChange={handleChangePage}
          rowsPerPage={filter.limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton={true}
          showLastButton={true}
        />
      </Box>
    </>
  );
}
